<template>
  <div class="custom-form custom-form--contact">
    <div class="custom-form__header">
      <slot name="content-introduction" />
    </div>
    <form @submit.prevent="submit"
          novalidate autocomplete="off">

      <field-radio class="gender-block"
                   v-model="form.gender"
                   :field="fieldGender" />

      <div class="columns">
        <div class="column" v-if="!fieldFirstName.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.firstName"
                         :v="$v.form.firstName"
                         :field="fieldFirstName" />
          </div>
        </div>

        <div class="column" v-if="!fieldLastName.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.lastName"
                         :v="$v.form.lastName"
                         :field="fieldLastName" />
          </div>
        </div>

        <div class="column" v-if="!fieldEmail.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.email"
                         :v="$v.form.email"
                         :field="fieldEmail" />
          </div>
        </div>

        <div class="column" />

        <div class="column" v-if="!fieldAddress.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.address"
                         :field="fieldAddress" />
          </div>
        </div>

        <div class="column" v-if="!fieldZipcode.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.zipcode"
                         :v="$v.form.zipcode"
                         :update-field="updateFields"
                         :field="fieldZipcode" />
          </div>
        </div>

        <div class="column" v-if="!fieldCity.isDisabled">
          <div class="fieldset">
            <field-input v-model="form.city"
                         :field="fieldCity" />
          </div>
        </div>

        <div class="column" v-if="!fieldState.isDisabled">
          <div class="fieldset">
            <Field-select :is-disabled="disableState"
                          v-model="form.state"
                          :field="fieldState" />
          </div>
        </div>

        <div class="column" v-if="!fieldCountry.isDisabled">
          <div class="fieldset">
            <Field-select v-model="form.country"
                          :field="fieldCountry" />
          </div>
        </div>

        <div class="column" v-if="!fieldPhone.isDisabled">
          <field-input v-model="form.phone"
                       :v="$v.form.phone"
                       :update-field="updateFields"
                       :field="fieldPhone" />
        </div>
      </div>

      <div class="fieldset" v-if="!fieldSubject.isDisabled">
        <field-input v-model="form.subject"
                     :v="$v.form.subject"
                     :field="fieldSubject" />
      </div>

      <div class="fieldset" v-if="!fieldRequest.isDisabled">
        <field-textarea v-model="form.request"
                        :v="$v.form.request"
                        :field="fieldRequest" />
      </div>

      <div class="custom-form__checkbox-group"
           v-for="(item, index) in fieldCheckboxes"
           :key="index">
        <field-checkbox v-if="item.required"
                        :value="form.gdpr[`checkbox-${item.gdprId}`]"
                        v-model="form.gdpr[`checkbox-${item.gdprId}`]"
                        :v="$v.form.gdpr[`checkbox-${item.gdprId}`]"
                        :field="item" />
        <field-checkbox v-else
                        :value="form.gdpr[`checkbox-${item.gdprId}`]"
                        v-model="form.gdpr[`checkbox-${item.gdprId}`]"
                        :field="item" />
      </div>

      <captcha ref="captcha"
               v-if="fieldCaptcha.isVisible"
               :captcha-id="fieldCaptcha.id"
               :captcha-sitekey="fieldCaptcha.siteKey"
               :explicitly-render="true"
               @captchaError="(value) => this.getCaptchaError(value)">
        <p class="field-help"
           v-if="hasErrorCapcha"
           :id="fieldCaptcha.idError">
          {{ fieldCaptcha.error }}
        </p>
      </captcha>

      <div class="custom-form__actions">
        <div>
          <template v-if="!serverResponse && serverError === null">
            <button type="submit"
              :class="[ctaClass, { 'disabled': $v.form.$invalid || formSubmit}]"
              :disabled="$v.form.$invalid || formSubmit"
              v-tag:useractionevent="{
                label: taggingEventLabel,
                category: 'form',
                action: 'submit',
                event_name: 'submit_contact_form',
                form_name: taggingEventLabel
              }">
              <span class="btn__wrapper">{{ template.ctaSubmit }}</span>
            </button>
          </template>

          <template v-else-if="template.ctaSuccess !== null">
            <a :href="ctaLink" :class="ctaClass">
              <span class="btn__wrapper">{{ template.ctaSuccess }}</span>
            </a>
          </template>
        </div>
        <div role="alert">
          <p v-if="serverResponse !== null"
              class="custom-form__response"
              :class="{ 'error': !serverResponse }">{{ serverResponse ? template.succeed : template.failure.default }}</p>
          <p v-if="serverError !== null"
              class="custom-form__response error">{{ template.error }}</p>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
  import { email, minLength, required, sameAs, helpers } from 'vuelidate/lib/validators';
  import { tag } from '@Foundation/analyticsHandler';
  import { validationMixin } from 'vuelidate';
  import { postData } from '@Foundation/services';
  import FieldCheckbox from '@Feature/CustomForms/fields/FieldCheckbox/code/Scripts/components/field-checkbox.vue';
  import FieldInput from '@Feature/CustomForms/fields/FieldInput/code/Scripts/components/field-input.vue';
  import FieldRadio from '@Feature/CustomForms/fields/FieldRadio/code/Scripts/components/field-radio.vue';
  import FieldSelect from '@Feature/CustomForms/fields/FieldSelect/code/Scripts/components/field-select.vue';
  import FieldTextarea from '@Feature/CustomForms/fields/FieldTextarea/code/Scripts/components/field-textarea.vue';
  import Captcha from '@Feature/Captcha/code/Scripts/components/captcha.vue';

  const alphaExtended = helpers.regex('alphaExtended', /^[a-zA-Zа-яА-ЯёЁіІàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]{3,50}$/u);

  export default {
    name: 'custom-form-contact',

    components: { FieldCheckbox, FieldInput, FieldRadio, FieldSelect, FieldTextarea, Captcha },

    directives: { tag },

    mixins: [validationMixin],

    props: {
      fieldEmail: { type: Object, required: true, default: undefined },
      fieldGender: { type: Object, required: true, default: undefined },
      fieldFirstName: { type: Object, required: true, default: undefined },
      fieldLastName: { type: Object, required: true, default: undefined },
      fieldCheckboxes: { type: Array, required: true, default: undefined },
      fieldAddress: { type: Object, required: true, default: undefined },
      fieldCity: { type: Object, required: true, default: undefined },
      fieldCountry: { type: Object, required: true, default: undefined },
      fieldZipcode: { type: Object, required: true, default: undefined },
      fieldState: { type: Object, required: true, default: undefined },
      fieldPhone: { type: Object, required: true, default: undefined },
      fieldSubject: { type: Object, required: true, default: undefined },
      fieldRequest: { type: Object, required: true, default: undefined },
      fieldCaptcha: { type: Object, required: false, default: undefined },
      template: { type: Object, required: true, default: undefined },
      apiUrl: { type: String, required: true, default: undefined },
      taggingEventLabel: { type: String, required: true, default: undefined },
    ctaClass: { type: String, default: 'btn btn--primary' },
    },

    data() {
      return {
        form: {
          email: '',
          gender: '',
          firstName: '',
          lastName: '',
          gdpr: {},
          address: '',
          city: '',
          country: '',
          zipcode: '',
          state: '',
          phone: '',
          subject: '',
          request: ''
        },
        zipCodeRegExp: '',
        phoneRegExp: '',
        disableState: false,
        serverResponse: null,
        serverError: null,
        hasErrorCapcha: false,
        updateFields: false,
        ctaLink: null,
        formSubmit: false
      };
    },

    /**
     * Vuelidate config
     * @see {@link https://github.com/monterail/vuelidate|GitHub}
     */
    validations() {
      const validationSettings = {
        form: {
          email: {
            required,
            email,
          },
          firstName: {
            required,
            alphaExtended,
            minLength: minLength(3),
          },
          lastName: {
            required,
            alphaExtended,
            minLength: minLength(3),
          },
          zipcode: {
            zipFormat: helpers.regex('zipFormat', new RegExp(this.zipCodeRegExp)) //custom
          },
          phone: {
            phoneFormat: helpers.regex('phoneFormat', new RegExp(this.phoneRegExp)) //custom
          },
          subject: {
            required
          },
          request: {
            required
          },
          gdpr: {}
        }
      };

      for (let index = 0; index < this.fieldCheckboxes.length; index++) {
        if (this.fieldCheckboxes[index].required) {
          validationSettings.form.gdpr[`checkbox-${this.fieldCheckboxes[index].gdprId}`] = {
            required,
            sameAs: sameAs(() => true),
          };
        }
      }

      this.fieldLastName.isDisabled ? validationSettings.form.lastName = {} : false;
      this.fieldFirstName.isDisabled ? validationSettings.form.firstName = {} : false;

      return validationSettings;
    },

    computed: {
      country() {
        return this.form.country;
      },
      state: {
        get() {
          return this.fieldState;
        },
        set(value) {
          this.state.listValue = value;
        }
      }
    },

    watch: {
      country(value) {
        this.getCountryData(value);
      }
    },


    created() {
      this.setCheckboxesToVueData();
      this.phoneRegExp = this.fieldPhone.regExp.replace(/\/\//g, '/');
      this.zipCodeRegExp = this.fieldZipcode.regExp;
      window.onCaptchaAPIReady = this.onCaptchaAPIReady;
    },

    /* eslint-disable no-undef */
    methods: {
      setCheckboxesToVueData() {
        this.fieldCheckboxes.forEach((item) => {
          this.$set(this.form.gdpr, `checkbox-${item.gdprId}`, item.value);
        });
      },

      getCaptchaError(val) {
        this.hasErrorCapcha = val;
      },

      /** check if captcha required for validation before sending a request
       * @param {function} callback - function to call after validation
       */
      validateCapcha(callback) {
        if (this.fieldCaptcha.isVisible) {
          this.$refs.captcha.checkCaptcha();

          this.$nextTick(() => {
            /* istanbul ignore else */
            if (!this.hasErrorCapcha) {
              callback();
            }
          });

        } else {
          callback();
        }
      },

      getCountryData(country) {
        this.disableState = true;
        this.form.state = '';
        postData(`${window.location.origin}/${this.fieldCountry.apiUrl}`, { country: country.trim() })
          .then((response) => {
            this.state = response.state;
            this.zipCodeRegExp = response.zipcode;
            this.phoneRegExp = response.phonereg.replace(/\/\//g, '/');

            setTimeout(() => {
              this.fieldPhone.info = response.phonereghelptext;
              this.fieldZipcode.info = response.zipcodehelptext;
            }, 100);

            /** trigger fields to update */
            setTimeout(() => {
              this.updateFields = true;
              this.$nextTick(() => {
                this.updateFields = false;
                /* eslint-disable no-console */
                console.log('update contact');
              });
            }, 300);

            this.disableState = false;
          })
          .catch(() => {
            this.serverError = true;
          });
      },

      sendRequest() {
        const gdpr = this.fieldCheckboxes.map((item, index) => {
          return {
            gdrpSettingItemId: this.fieldCheckboxes[index].gdprId,
            isSelected: this.form.gdpr[`checkbox-${item.gdprId}`]
          };
        });
        postData(`${window.location.origin}/${this.apiUrl}`, { ...this.form, gdpr })
          .then((response) => {
            this.serverResponse = response.status;
            /* istanbul ignore else */
            if (this.serverResponse && !!response.url) {
              this.ctaLink = response.url;
            }
            this.formSubmit = false;
          })
          .catch(() => {
            this.formSubmit = false;
            this.serverError = true;
          });
      },

      /**
       * @todo
       * @returns {undefined}
       */
      submit() {
        this.$v.form.$touch();
        if (this.$v.form.$pending || this.$v.form.$error) {
          return;
        } else {
          this.formSubmit = true;
          this.validateCapcha(this.sendRequest);
        }
      }
    }
  };
</script>

<style lang='scss' src="./custom-form-contact.scss"></style>
